import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, Heart, History, 
  Volume2, Play, Square, Check, Settings, ScanFace, ShieldCheck, Trash2, Speaker
} from "lucide-react";
import { getSoundPrefs, setSoundPrefs, playSound, SOUND_TYPES, playCelebrationChime } from "../lib/soundEngine";
import { getNarrationPref, setNarrationPref, playNarration, stopNarration } from "../lib/narrationEngine";
import { isWebAuthnSupported, getStoredCredentialId, registerBiometric, clearStoredCredential } from "../lib/webauthn";
import axios from "axios";
import MembershipCard from "../components/MembershipCard";
import { PushSettingsCard } from "../components/PushSettingsCard";
import { VipBadge } from "../components/VipBadge";
import { PracticeRemindersCard } from "../components/PracticeRemindersCard";
import { ShareInviteButton } from "../components/ShareInviteButton";
import { PrivacySettingsCard } from "../components/PrivacySettingsCard";
import { AppMenu } from "../components/AppMenu";
import logger from "../lib/logger";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // When the user lands on /settings#delete-account (e.g. from the Account
  // page's "Privacy & data" tile, or from the App Store reviewer following
  // our submission notes), smoothly scroll to the deletion card so it's
  // immediately visible rather than buried below the rest of Settings.
  useEffect(() => {
    if (location.hash === "#delete-account") {
      // wait one tick so the card has mounted
      const t = setTimeout(() => {
        document
          .getElementById("delete-account")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  // When coming from the bilateral sound banner during an active session,
  // show a trimmed view (sound type + volume only) with a "Return to Session" button.
  const returnTo = searchParams.get('returnTo');
  const isSessionMode = Boolean(returnTo);

  const [prefs, setPrefs] = useState(getSoundPrefs);
  const [previewingId, setPreviewingId] = useState(null);
  const [narrateAuto, setNarrateAuto] = useState(getNarrationPref);
  const audioCtxRef = useRef(null);
  const previewIntervalRef = useRef(null);
  
  // Biometric state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  useEffect(() => {
    async function checkBiometric() {
      const supported = await isWebAuthnSupported();
      setBiometricSupported(supported);
      if (supported) {
        const credId = getStoredCredentialId();
        if (credId) {
          try {
            const res = await axios.get(`${API}/webauthn/credentials`, { withCredentials: true });
            setBiometricEnabled(res.data.length > 0);
          } catch {
            setBiometricEnabled(false);
          }
        }
      }
    }
    checkBiometric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnableBiometric = async () => {
    setBiometricLoading(true);
    try {
      await registerBiometric();
      setBiometricEnabled(true);
      toast.success("Face ID / Biometric login enabled! You can now sign in with your face or fingerprint.");
    } catch (error) {
      logger.error("Biometric registration failed:", error);
      if (error.name === "NotAllowedError") {
        toast.error("Biometric registration was cancelled or denied.");
      } else {
        toast.error("Failed to set up biometric login. Please try again.");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDisableBiometric = async () => {
    setBiometricLoading(true);
    try {
      await axios.delete(`${API}/webauthn/credentials`, { withCredentials: true });
      clearStoredCredential();
      setBiometricEnabled(false);
      toast.success("Biometric login disabled.");
    } catch (error) {
      logger.error("Failed to disable biometric:", error);
      toast.error("Failed to disable biometric login.");
    } finally {
      setBiometricLoading(false);
    }
  };

  const getCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stopPreview = useCallback(() => {
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }
    setPreviewingId(null);
  }, []);

  useEffect(() => {
    return () => {
      stopPreview();
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close();
      }
    };
  }, [stopPreview]);

  const playPreview = (soundId) => {
    // If already previewing this sound, stop
    if (previewingId === soundId) {
      stopPreview();
      return;
    }
    stopPreview();
    setPreviewingId(soundId);

    const ctx = getCtx();
    const BPM = 112;
    const interval = 60000 / BPM;
    let beatIndex = 0;
    const totalBeats = Math.floor((3000 / interval)); // ~3 seconds worth

    // Play first beat immediately
    playSound(ctx, soundId, "left", prefs.volume);

    previewIntervalRef.current = setInterval(() => {
      beatIndex++;
      const side = beatIndex % 2 === 0 ? "left" : "right";
      playSound(ctx, soundId, side, prefs.volume);

      if (beatIndex >= totalBeats) {
        stopPreview();
      }
    }, interval);
  };

  const updateVolume = (val) => {
    const newPrefs = { ...prefs, volume: val[0] / 100 };
    setPrefs(newPrefs);
    setSoundPrefs(newPrefs);
  };

  const updateVoiceVolume = (val) => {
    const newPrefs = { ...prefs, voiceVolume: val[0] / 100 };
    setPrefs(newPrefs);
    setSoundPrefs(newPrefs);
  };

  const selectSound = (soundId) => {
    const newPrefs = { ...prefs, soundType: soundId };
    setPrefs(newPrefs);
    setSoundPrefs(newPrefs);
    toast.success(`Sound changed to ${SOUND_TYPES.find(s => s.id === soundId)?.name}`);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="settings-page">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3090A8] rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
          </div>
          
          {!isSessionMode && (
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#718096] hover:text-[#3090A8] transition-colors" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/history" className="text-[#718096] hover:text-[#3090A8] transition-colors" data-testid="nav-history">
                History
              </Link>
              <Link to="/favorites" className="text-[#718096] hover:text-[#3090A8] transition-colors" data-testid="nav-favorites">
                Favorites
              </Link>
              <Link to="/settings" className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors" data-testid="nav-settings">
                Settings
              </Link>
            </nav>
          )}
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            )}
            <AppMenu />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 pb-24 app-prose">
        {/* Back + Title */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(isSessionMode ? returnTo : '/dashboard')}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
          </button>
          <div>
            <h1 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] flex items-center gap-3">
              <span>{isSessionMode ? 'Sound Settings' : 'Settings'}</span>
              {!isSessionMode && <VipBadge user={user} size="lg" />}
            </h1>
            <p className="text-[#718096]">
              {isSessionMode
                ? 'Adjust sound and volume during your session'
                : 'Customize your bilateral stimulation experience'}
            </p>
          </div>
        </div>

        {/* Membership card — only shown outside of an active intervention session */}
        {!isSessionMode && <MembershipCard />}

        {/* Volume Control */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up">
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
            Bilateral Sound Volume
          </h2>
          <p className="text-sm text-[#718096] mb-5">
            Adjust the default volume for bilateral stimulation sounds
          </p>
          <div className="flex items-center gap-4">
            <Volume2 className="w-5 h-5 text-[#3090A8] flex-shrink-0" />
            <Slider
              value={[prefs.volume * 100]}
              onValueChange={updateVolume}
              max={100}
              step={1}
              className="flex-1"
              data-testid="settings-volume-slider"
            />
            <span className="text-sm font-medium text-[#1B2349] w-10 text-right">
              {Math.round(prefs.volume * 100)}%
            </span>
          </div>
        </div>

        {/* iOS push notifications toggle (hidden on web) */}
        <PushSettingsCard />

        {/* Practice reminders (daily nudges) — iOS local notifications + web browser API */}
        {!isSessionMode && <PracticeRemindersCard />}

        {/* Invite a friend — native share sheet with clipboard fallback */}
        {!isSessionMode && <ShareInviteButton variant="inline" />}

        {/* Voice volume — unified slider for the breath cue AND the
            narration voice. Both sources tap into the same
            soundPrefs.voiceVolume value, so moving this slider applies
            to "Take a deep breath" cues, task narration, AND hands-
            free check-in prompts. */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up">
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
            Voice volume
          </h2>
          <p className="text-sm text-[#718096] mb-5">
            Sets the volume for both the "take a deep breath" cue and the spoken task narration.
          </p>
          <div className="flex items-center gap-4">
            <Volume2 className="w-5 h-5 text-[#78C0D8] flex-shrink-0" />
            <Slider
              value={[(prefs.voiceVolume ?? 0.55) * 100]}
              onValueChange={updateVoiceVolume}
              max={100}
              step={1}
              className="flex-1"
              data-testid="settings-voice-volume-slider"
            />
            <span className="text-sm font-medium text-[#1B2349] w-10 text-right">
              {Math.round((prefs.voiceVolume ?? 0.55) * 100)}%
            </span>
          </div>
        </div>

        {/* Sound Type Selection */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up stagger-1">
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
            Sound Type
          </h2>
          <p className="text-sm text-[#718096] mb-5">
            Choose your preferred bilateral stimulation sound. Play a 3-second preview of each.
          </p>

          <div className="space-y-3">
            {SOUND_TYPES.map((sound) => {
              const isSelected = prefs.soundType === sound.id;
              const isPreviewing = previewingId === sound.id;

              return (
                <div
                  key={sound.id}
                  className={`rounded-xl p-4 border-2 transition-all duration-200 ${
                    isSelected 
                      ? 'border-[#3090A8] bg-[#E8F0E7]/40' 
                      : 'border-slate-100 hover:border-slate-200 bg-white'
                  }`}
                  data-testid={`sound-option-${sound.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Select radio */}
                      <button
                        onClick={() => selectSound(sound.id)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected 
                            ? 'border-[#3090A8] bg-[#3090A8]' 
                            : 'border-slate-300 hover:border-[#3090A8]'
                        }`}
                        data-testid={`select-sound-${sound.id}`}
                      >
                        {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                      </button>

                      <div className="min-w-0">
                        <p className="font-medium text-[#1B2349]">{sound.name}</p>
                        <p className="text-sm text-[#718096] truncate">{sound.description}</p>
                      </div>
                    </div>

                    {/* Preview button */}
                    <Button
                      onClick={() => playPreview(sound.id)}
                      variant="outline"
                      size="sm"
                      className={`rounded-full flex-shrink-0 ml-3 ${
                        isPreviewing 
                          ? 'border-[#D98880] text-[#D98880] hover:bg-[#D98880] hover:text-white'
                          : 'border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white'
                      }`}
                      data-testid={`preview-sound-${sound.id}`}
                    >
                      {isPreviewing ? (
                        <>
                          <Square className="w-3.5 h-3.5 mr-1.5" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="w-3.5 h-3.5 mr-1.5" />
                          Preview
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Celebration Chime Toggle */}
        <div
          className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mt-6 animate-fade-in-up stagger-1"
          data-testid="celebration-sound-section"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
                Celebration Chime
              </h2>
              <p className="text-sm text-[#718096]">
                Plays a soft, brief chime when confetti or fireworks appear between tasks. Turn it off if you'd prefer a silent celebration.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.celebrationSound !== false}
              onClick={() => {
                const next = prefs.celebrationSound === false;
                const newPrefs = { ...prefs, celebrationSound: next };
                setPrefs(newPrefs);
                setSoundPrefs(newPrefs);
                if (next) {
                  // Preview the chime so the user hears what it sounds like.
                  try { playCelebrationChime(prefs.volume ?? 0.5); }
                  catch (e) { logger.warn("celebration preview failed:", e); }
                }
              }}
              className={`relative inline-flex items-center h-7 w-12 flex-shrink-0 rounded-full transition-colors duration-200 ${
                prefs.celebrationSound !== false ? 'bg-[#3090A8]' : 'bg-slate-300'
              }`}
              data-testid="celebration-sound-toggle"
            >
              <span
                className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                  prefs.celebrationSound !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Auto-narrate task text toggle.
            When enabled, every task page automatically reads its title +
            instruction text aloud in a calm voice (OpenAI TTS 'sage',
            slow speed). Per-task speaker button on each task screen
            stays available either way. */}
        <div
          className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mt-6 animate-fade-in-up stagger-1"
          data-testid="narration-section"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
                <Speaker className="w-5 h-5 text-[#3090A8]" />
                Narrate task text aloud
              </h2>
              <p className="text-sm text-[#718096]">
                Have a calm voice read each practice task to you so you can rest your eyes. The speaker icon on every task screen still works on demand even when this is off.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={narrateAuto}
              onClick={() => {
                const next = !narrateAuto;
                setNarrateAuto(next);
                setNarrationPref(next);
              }}
              className={`relative inline-flex items-center h-7 w-12 flex-shrink-0 rounded-full transition-colors duration-200 ${
                narrateAuto ? 'bg-[#3090A8]' : 'bg-slate-300'
              }`}
              data-testid="narration-toggle"
            >
              <span
                className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                  narrateAuto ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Biometric Login Section — only shown outside an active session */}
        {!isSessionMode && biometricSupported && (
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mt-6 animate-fade-in-up stagger-2">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#EAF0F9] rounded-xl flex items-center justify-center flex-shrink-0">
                <ScanFace className="w-6 h-6 text-[#78C0D8]" />
              </div>
              <div className="flex-1">
                <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
                  Face ID / Biometric Login
                </h2>
                <p className="text-sm text-[#718096] mb-4">
                  {biometricEnabled
                    ? "Biometric login is enabled on this device. You can sign in with Face ID, Touch ID, or Windows Hello without needing Google sign-in."
                    : "Enable fast re-login using your device's face recognition, fingerprint, or Windows Hello. Your biometric data never leaves your device."
                  }
                </p>

                {biometricEnabled ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-[#3090A8] bg-[#E8F0E7] px-3 py-1.5 rounded-full text-sm font-medium">
                      <ShieldCheck className="w-4 h-4" />
                      Enabled
                    </div>
                    <Button
                      onClick={handleDisableBiometric}
                      disabled={biometricLoading}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#D98880] text-[#D98880] hover:bg-[#D98880] hover:text-white"
                      data-testid="disable-biometric-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      {biometricLoading ? "Removing..." : "Disable"}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleEnableBiometric}
                    disabled={biometricLoading}
                    className="btn-secondary"
                    data-testid="enable-biometric-btn"
                  >
                    <ScanFace className="w-4 h-4 mr-2" />
                    {biometricLoading ? "Setting up..." : "Enable Face ID / Biometric Login"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Return to Session button — only in session mode */}
        {isSessionMode && (
          <div className="mt-6">
            <Button
              onClick={() => navigate(returnTo)}
              className="w-full btn-primary text-base py-5 rounded-full"
              data-testid="return-to-session-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Session
            </Button>
          </div>
        )}

        {/* Privacy & data controls — always last so destructive actions
            (delete account) live below everything benign. */}
        {!isSessionMode && <PrivacySettingsCard />}
      </main>

      {/* Mobile Navigation — hidden in session mode to keep focus */}
      {!isSessionMode && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3">
          <div className="flex items-center justify-around">
            <Link to="/dashboard" className="flex flex-col items-center gap-1 text-[#718096]">
              <Sparkles className="w-5 h-5" />
              <span className="text-xs">Home</span>
            </Link>
            <Link to="/history" className="flex flex-col items-center gap-1 text-[#718096]">
              <History className="w-5 h-5" />
              <span className="text-xs">History</span>
            </Link>
            <Link to="/favorites" className="flex flex-col items-center gap-1 text-[#718096]">
              <Heart className="w-5 h-5" />
              <span className="text-xs">Favorites</span>
            </Link>
            <Link to="/settings" className="flex flex-col items-center gap-1 text-[#3090A8]">
              <Settings className="w-5 h-5" />
              <span className="text-xs">Settings</span>
            </Link>
          </div>
        </nav>
      )}
      <SiteFooter />
    </div>
  );
};

export default SettingsPage;
